import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { formatXAF, formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { KeyRound, Plus, Copy, Pause, Play, Trash2 } from "lucide-react";

const PLANS = ["essentiel", "professionnel", "franchise"];

export default function Licences() {
  const { t, lang } = useI18n();
  const [licenses, setLicenses] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [newL, setNewL] = useState({ plan: "professionnel", franchisee_name: "", franchisee_email: "", business_model: "", expires_at: "" });

  async function load() {
    const [l, p] = await Promise.all([api.get("/licenses"), api.get("/public/license-plans")]);
    setLicenses(l.data); setPlans(p.data);
  }
  useEffect(() => { load(); }, []);

  async function generate() {
    if (!newL.franchisee_name) return toast.error("Franchisé requis");
    try {
      const { data } = await api.post("/licenses/generate", newL);
      setOpen(false); setNewL({ plan: "professionnel", franchisee_name: "", franchisee_email: "", business_model: "", expires_at: "" });
      toast.success(`${t("license_key")}: ${data.key}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }
  async function suspend(id) {
    await api.post(`/licenses/${id}/suspend`);
    toast.success(t("updated")); load();
  }
  async function reactivate(id) {
    await api.post(`/licenses/${id}/reactivate`);
    toast.success(t("updated")); load();
  }
  async function delLic(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/licenses/${id}`);
    toast.success(t("deleted")); load();
  }
  function copyKey(key) {
    navigator.clipboard.writeText(key);
    toast.success(t("copied"));
  }

  const STATUS_VARIANT = { "active": "default", "activée": "default", "suspendue": "secondary", "expirée": "destructive" };

  return (
    <div className="space-y-6" data-testid="licenses-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">{t("franchise_model")}</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
            <KeyRound className="w-8 h-8 text-primary" />{t("licenses")}
          </h1>
          <p className="text-muted-foreground mt-1">{licenses.length} {t("licenses").toLowerCase()}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="generate-license-btn"><Plus className="w-4 h-4 mr-2" />{t("generate_license")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("generate_license")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t("plan")}</Label>
                <Select value={newL.plan} onValueChange={(v) => setNewL({ ...newL, plan: v })}>
                  <SelectTrigger data-testid="license-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>{plans.map((p) => <SelectItem key={p.key} value={p.key}>{p.label} — {formatXAF(p.price)}/{t("month")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("franchisee_name")}</Label><Input data-testid="license-franchisee" value={newL.franchisee_name} onChange={(e) => setNewL({ ...newL, franchisee_name: e.target.value })} /></div>
              <div><Label>{t("email")}</Label><Input type="email" value={newL.franchisee_email} onChange={(e) => setNewL({ ...newL, franchisee_email: e.target.value })} /></div>
              <div><Label>{t("business_model")}</Label><Input data-testid="license-model" placeholder="Agriculture + Apiculture…" value={newL.business_model} onChange={(e) => setNewL({ ...newL, business_model: e.target.value })} /></div>
              <div><Label>{t("expires_at")}</Label><Input type="date" value={newL.expires_at} onChange={(e) => setNewL({ ...newL, expires_at: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={generate} data-testid="license-save">{t("save")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left border-b border-border bg-secondary/50">
              <tr>
                <th className="p-3">{t("license_key")}</th><th>{t("plan")}</th>
                <th>{t("franchisee_name")}</th><th>{t("payment_status")}</th>
                <th>{t("status")}</th><th>{t("date")}</th><th>{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {licenses.map((l) => (
                <tr key={l.id} className="border-b border-border/60 last:border-0" data-testid={`license-row-${l.id}`}>
                  <td className="p-3 font-mono font-semibold flex items-center gap-2">
                    {l.key}
                    <button onClick={() => copyKey(l.key)} className="text-muted-foreground hover:text-primary" data-testid={`license-copy-${l.id}`}>
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </td>
                  <td><Badge variant="secondary">{l.plan_label}</Badge></td>
                  <td>
                    <div className="font-medium">{l.franchisee_name}</div>
                    <div className="text-xs text-muted-foreground">{l.business_model || l.franchisee_email}</div>
                  </td>
                  <td><Badge variant={l.payment_status === "payée" ? "default" : "outline"}>{t(l.payment_status) || l.payment_status}</Badge></td>
                  <td><Badge variant={STATUS_VARIANT[l.status] || "secondary"}>{t(`lic_${l.status}`) || l.status}</Badge></td>
                  <td className="text-muted-foreground">{formatDate(l.created_at, lang)}</td>
                  <td className="whitespace-nowrap">
                    {l.status === "suspendue" ? (
                      <Button variant="ghost" size="sm" onClick={() => reactivate(l.id)} data-testid={`license-reactivate-${l.id}`}><Play className="w-4 h-4" /></Button>
                    ) : (
                      <Button variant="ghost" size="sm" onClick={() => suspend(l.id)} data-testid={`license-suspend-${l.id}`}><Pause className="w-4 h-4" /></Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => delLic(l.id)}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
              {licenses.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-sm text-muted-foreground">{t("no_data")}</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
